import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import 'boxicons';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { useAuth } from 'hooks/useAuth';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { MoonLoader } from 'react-spinners';

import { TaskSummmaryLayout } from './styles';
import ButtonFilter from 'pages/guideline/components/Button-Filter';
import { GDriveIcon } from 'assets/icons';
import { handleDownloadGuidelineCSV, handleDownloadTaskCSV } from 'utils';
import HeaderContext from '../Tasks-Filter/TasksFilter.Context';

const TasksSummary = ({
  board,
  toggleFilter,
  filterTotal,
  isContent,
  isGuideline,
  show,
  userStatus,
}) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const accountId = user?.account;
  const firebaseHelper = getFirebaseBackend();
  const { statuses, hiddenColumns, setHiddenColumns } = useContext(HeaderContext);
  const [gDriveContentLink, setGDriveContentLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const toggleColumn = (columnId) => {
    if (hiddenColumns.includes(columnId)) {
      setHiddenColumns(hiddenColumns.filter((id) => id !== columnId));
    } else {
      setHiddenColumns([...hiddenColumns, columnId]);
    }
  };

  const canDownloadGuidelineCSV = () => {
    return ['Super Admin', 'Admin'].includes(userStatus);
  };

  const canCreateGuideline = () => {
    return ['Super Admin'].includes(userStatus);
  };

  const canOpenDrive = () => {
    return ['Super Admin', 'Admin'].includes(userStatus);
  };

  let tasksTotal = 0;

  let columnTotals = {};

  // Counts tasks totals
  board?.columns.forEach((column) => {
    column.cards.forEach((task) => {
      columnTotals[task.status.status] = (columnTotals[task.status.status] || 0) + 1;
      tasksTotal++;
    });
  });

  const handleToggle = () => toggleFilter((prevState) => !prevState);

  const getGDriveLink = async () => {
    const account = await firebaseHelper.getAccount(accountId);
    if (account?.gDriveFolders) {
      isGuideline
        ? setGDriveContentLink(account?.gDriveFolders?.content?.delivery)
        : setGDriveContentLink(account?.gDriveFolders?.seo?.delivery);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      setIsLoading(true);
      isGuideline
        ? await handleDownloadGuidelineCSV(accountId)
        : await handleDownloadTaskCSV(accountId);
    } catch (error) {
      console.error('Error downloading CSV:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getGDriveLink();
  }, [accountId]);

  return (
    <TaskSummmaryLayout className="header-wrapper" style={{ display: show ? 'flex' : 'none' }}>
      {isGuideline ? (
        <div className="task-summary-guideline">
          <div className="task-summary-stats">
            <div className="task-summary-stat">
              <p className="task-summary-title">{translate('Total Guidelines')}</p>
              <p className="task-summary-number">{tasksTotal}</p>
            </div>
            {board.columns.slice(1, 8).map((column) => {
              return (
                <div className="task-summary-stat" key={column.id}>
                  <p className="task-summary-title">{translate(column.title)}</p>
                  <p className="task-summary-number">{column.cards.length}</p>
                </div>
              );
            })}
          </div>
          <div className="task-summary-btns">
            {canOpenDrive() && (
              <Button
                className="btn-drive"
                onClick={() => window.open(gDriveContentLink, '_blank')}
              >
                <GDriveIcon />
                {`${translate('Open')} Drive`}
              </Button>
            )}
            {canDownloadGuidelineCSV() && (
              <Button className="btn-download-csv" onClick={handleDownloadCSV}>
                {isLoading ? (
                  <MoonLoader size={20} color="#2d3748" loading={isLoading} />
                ) : (
                  <>
                    <i className="bx bx-download" />
                    {translate('Export CSV')}
                  </>
                )}
              </Button>
            )}
            <ButtonFilter handleToggle={handleToggle} filterTotal={filterTotal} />
            {canCreateGuideline() && (
              <Button
                className="btn-guideline-create"
                onClick={() => navigate('/create-guideline')}
              >
                {translate('Create Guideline')}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.99834 2C3.99834 3.47337 2.80394 4.66778 1.33057 4.66778C2.80394 4.66778 3.99834 5.86218 3.99834 7.33556C3.99834 5.86218 5.19275 4.66778 6.66612 4.66778C5.19275 4.66778 3.99834 3.47337 3.99834 2Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11.3347 10.0054C11.3347 8.16367 9.84172 6.67066 8 6.67066C9.84172 6.67066 11.3347 5.17765 11.3347 3.33594C11.3347 5.17765 12.8277 6.67066 14.6694 6.67066C12.8277 6.67066 11.3347 8.16367 11.3347 10.0054Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.66607 9C6.66607 10.4734 5.47166 11.6678 3.99829 11.6678C5.47166 11.6678 6.66607 12.8622 6.66607 14.3356C6.66607 12.8622 7.86047 11.6678 9.33385 11.6678C7.86047 11.6678 6.66607 10.4734 6.66607 9Z"
                    fill="white"
                  />
                </svg>
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="task-summary-guideline">
          <div className="task-summary-stats">
            <div className="task-summary-stat">
              <p className="task-summary-title">{translate('Total Tasks')}</p>
              <p className="task-summary-number">{tasksTotal}</p>
            </div>

            {columnTotals &&
              Object.entries(columnTotals).map(([key, value]) => (
                <div className="task-summary-stat" key={key}>
                  <p className="task-summary-title" style={{ textTransform: 'capitalize' }}>
                    {key}
                  </p>
                  <p className="task-summary-number">{value}</p>
                </div>
              ))}
          </div>
          <div className="task-summary-btns">
            {canOpenDrive() && (
              <Button
                className="btn-drive"
                onClick={() => window.open(gDriveContentLink, '_blank')}
              >
                <GDriveIcon />
                {`${translate('Open')} Drive`}
              </Button>
            )}
            <ButtonFilter handleToggle={handleToggle} filterTotal={filterTotal} />
            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
              <DropdownToggle color="outline" style={{ width: '44px', height: '44px' }}>
                <i className="bx bx-cog" style={{ fontSize: '1rem' }} />
              </DropdownToggle>
              <DropdownMenu
                className={dropdownOpen ? 'd-flex flex-column gap-2 px-0 py-2 mt-1' : 'd-none'}
                style={{ minWidth: '18rem' }}
                end
              >
                {statuses.map((status) => (
                  <DropdownItem
                    key={status.id}
                    className="d-flex align-items-center gap-2 px-3 py-1 hover-gray w-100 justify-content-start"
                    onClick={() => toggleColumn(status.id)}
                  >
                    <FormGroup switch key={status.id}>
                      <Input
                        id={`column-toggle-${status.id}`}
                        type="switch"
                        checked={!hiddenColumns.includes(status.id)}
                        readOnly
                      />
                      <Label
                        for={`column-toggle-${status.id}`}
                        className="m-0 flex-grow-1"
                        style={{ textTransform: 'capitalize' }}
                      >
                        {status.status}
                      </Label>
                    </FormGroup>
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      )}
    </TaskSummmaryLayout>
  );
};

TasksSummary.propTypes = {
  board: PropTypes.object,
  toggleFilter: PropTypes.func,
  show: PropTypes.bool,
  isContent: PropTypes.bool,
  isGuideline: PropTypes.bool,
  filterTotal: PropTypes.func,
  userStatus: PropTypes.string,
};

export default TasksSummary;
